import React, { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import qz from "qz-tray";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useQzPrint } from "src/hooks/useQzPrint";
import { Tab } from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import {
	defaultEqmInspectResultHeaderDto,
	EqmInspectResultDetailDto,
	EqmInspectResultHeaderDto,
} from "src/dtos/eqmInspectResult/eqmInspectResultDto";
import {
	EQM2070_R01DetailDto,
	EQM2070_R01DetailPivotDto,
	EQM2070_R01Dto,
	EQM2070_R01InspectMasterDto,
} from "./Dto_EQM2070_R01";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import {
	downloadExcel_EQM2070_R01,
	getEQM2070_R01AllCount,
	getEQM2070_R01DetailList,
	getEQM2070_R01InspectList,
	getEQM2070_R01List,
	getPdfBlob_EQM2070_R01,
	patchEQM2070_R01,
	postEQM2070_R01,
} from "./Api_EQM2070_R01";
import { EQM2070_R01Filter, useEQM2070_R01Store } from "./Store_EQM2070_R01";
import {
	flattenGridRowsToDetail,
	pivotDetailToGridRows,
} from "./Util_EQM2070_R01";

interface Props {
	detailGridRef: React.RefObject<AgGridReact<EQM2070_R01DetailPivotDto> | null>;
	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;
}

export const Content_EQM2070_R01 = (props: Props) => {
	const { tabList, setMessage } = props;
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const {
		EQM2070_R01Filter,
		setEQM2070_R01PastFilter,
		EQM2070_R01List,
		setEQM2070_R01List,
		setEditEQM2070_R01,
		editEQM2070_R01,
		selectedEQM2070_R01,
		setSelectedEQM2070_R01,
		EQM2070_R01DetailPivotList,
		setEQM2070_R01DetailPivotList,
		setOriginalEQM2070_R01DetailPivotList,
		setAllCount,
		initData,
		setDetailScrollTop,
	} = useEQM2070_R01Store();

	useFetchActionbarAuth("EQM2070_R01");
	const [clickable, setClickable] = useState(true);
	const originalFlatDetailListRef = useRef<EQM2070_R01DetailDto[]>([]);
	const hasOriginalFlatDetailRef = useRef(false);
	const inspectMasterRef = useRef<EQM2070_R01InspectMasterDto[]>([]);
	const prevSearchKeyRef = useRef(
		`${EQM2070_R01Filter.inspectDt}|${EQM2070_R01Filter.equipmentCd}`,
	);

	const getParams = (filter: EQM2070_R01Filter) => ({
		compCd: filter.compCd || undefined,
		inspectDt: filter.inspectDt || undefined,
		equipmentCd: filter.equipmentCd || undefined,
	});

	const renewalAllCount = useCallback(async () => {
		const response = await getEQM2070_R01AllCount(getParams(EQM2070_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [EQM2070_R01Filter, setAllCount]);

	const applyDetailPivot = useCallback((
		flatList: EQM2070_R01DetailDto[],
		inspectMasterList: EQM2070_R01InspectMasterDto[] = inspectMasterRef.current,
	) => {
		const pivotRows = pivotDetailToGridRows(
			flatList,
			inspectMasterList,
			EQM2070_R01Filter.inspectDt,
		);
		setEQM2070_R01DetailPivotList(pivotRows);
		setOriginalEQM2070_R01DetailPivotList(pivotRows);
	}, [EQM2070_R01Filter.inspectDt, setEQM2070_R01DetailPivotList, setOriginalEQM2070_R01DetailPivotList]);

	const fetchInspectMaster = useCallback(async () => {
		const responseData = (await getEQM2070_R01InspectList(getParams(EQM2070_R01Filter))).data.data;
		if (responseData?.length > 0) {
			const inspectMasterList: EQM2070_R01InspectMasterDto[] = responseData.map((value) => ({
				eqmType: value.eqmType || "",
				eqmTypeNm: value.eqmTypeNm || "",
				inspectCd: value.inspectCd || "",
				inspectNm: value.inspectNm || "",
			}));
			inspectMasterRef.current = inspectMasterList;
			setMessage(
				`${EQM2070_R01Filter.equipmentNm || EQM2070_R01Filter.equipmentCd}의 점검코드 ${inspectMasterList.length}건을 조회하였습니다.`,
			);
			setTimeout(() => setMessage(""), 5000);
			return inspectMasterList;
		}

		inspectMasterRef.current = [];
		return [];
	}, [EQM2070_R01Filter, setMessage]);

	const fetchDetailList = useCallback(async (inspectNo: string) => {
		const responseData = (await getEQM2070_R01DetailList(inspectNo)).data.data ?? [];
		originalFlatDetailListRef.current = responseData;
		hasOriginalFlatDetailRef.current = true;
		applyDetailPivot(responseData);

		if (responseData.length > 0) {
			setMessage(
				`${selectedEQM2070_R01?.equipmentNm}의 점검결과 상세 ${responseData.length}건을 조회하였습니다.`,
			);
			setTimeout(() => setMessage(""), 5000);
		}
	}, [selectedEQM2070_R01, applyDetailPivot, setMessage]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setDetailScrollTop(null);

		const responseData = await getEQM2070_R01List(
			"search",
			getParams(EQM2070_R01Filter),
			EQM2070_R01List.length ?? 0,
		);
		const newList = [...responseData.data.data] as EqmInspectResultHeaderDto[];
		setEQM2070_R01List(newList);

		const processAllCount = await renewalAllCount();
		await fetchInspectMaster();

		if (newList.length > 0) {
			setSelectedEQM2070_R01(newList[0]);
			await fetchDetailList(newList[0].inspectNo);
			setEQM2070_R01PastFilter(EQM2070_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning("데이터가 없습니다.");
			setSelectedEQM2070_R01(undefined);
			setEQM2070_R01List([]);
			originalFlatDetailListRef.current = [];
			hasOriginalFlatDetailRef.current = false;
			if (inspectMasterRef.current.length > 0) {
				applyDetailPivot([], inspectMasterRef.current);
			} else {
				setEQM2070_R01DetailPivotList([]);
				setOriginalEQM2070_R01DetailPivotList([]);
				inspectMasterRef.current = [];
			}
		}
		setLoadingSpinner(false);
		return responseData;
	}, [EQM2070_R01Filter, EQM2070_R01List, fetchInspectMaster, fetchDetailList, setEQM2070_R01List, setEQM2070_R01PastFilter, setSelectedEQM2070_R01, setLoadingSpinner, setMessage, setDetailScrollTop, renewalAllCount, applyDetailPivot, setEQM2070_R01DetailPivotList, setOriginalEQM2070_R01DetailPivotList]);

	const initContainerData = useCallback(() => {
		initData();
		setEditEQM2070_R01({ ...defaultEqmInspectResultHeaderDto });
		originalFlatDetailListRef.current = [];
		hasOriginalFlatDetailRef.current = false;
		inspectMasterRef.current = [];
	}, [initData, setEditEQM2070_R01]);

	const handleInsertClick = useCallback(() => {
		showWarning("점검항목은 점검코드 목록 기준으로 표시됩니다.");
	}, []);

	const handleDeleteClick = useCallback(() => {
		showWarning("일자별 점검값은 셀에서 직접 삭제하세요.");
	}, []);

	const handleCreateClick = useCallback(() => {
		setSelectedEQM2070_R01(undefined);
		setEditEQM2070_R01({ ...defaultEqmInspectResultHeaderDto });
		originalFlatDetailListRef.current = [];
		hasOriginalFlatDetailRef.current = false;
		applyDetailPivot([], inspectMasterRef.current);
	}, [setSelectedEQM2070_R01, setEditEQM2070_R01, applyDetailPivot]);

	const validateInsertData = () => {
		if (!validateString(editEQM2070_R01?.judgeEmpno || "")) {
			showWarning("담당자를 입력하세요.");
			return false;
		}
		return true;
	};

	const handleSaveClick = useCallback(async () => {
		const hasMismatchedEquipmentType = EQM2070_R01DetailPivotList.some(
			(row) => row.eqmType && row.eqmType !== EQM2070_R01Filter.equipmentCd,
		);
		if (hasMismatchedEquipmentType) {
			const message = "선택한 탭과 점검항목의 설비유형이 일치하지 않습니다. 다시 조회하세요.";
			showWarning(message);
			setMessage(message);
			return;
		}
		if (!validateInsertData()) return;

		setLoadingSpinner(true);

		try {
			const currentFlat = flattenGridRowsToDetail(
				EQM2070_R01DetailPivotList,
				{
					inspectNo: editEQM2070_R01?.inspectNo || "",
					compCd: EQM2070_R01Filter.compCd || "",
					equipmentCd: EQM2070_R01Filter.equipmentCd || "",
					inspectCycle: editEQM2070_R01?.inspectCycle || "",
					judgeEmpno: editEQM2070_R01?.judgeEmpno || "",
					judgeEmpNm: editEQM2070_R01?.judgeEmpNm || "",
				},
				EQM2070_R01Filter.inspectDt,
			);

			const payloadBase = {
				inspectNo: editEQM2070_R01?.inspectNo || "",
				compCd: EQM2070_R01Filter.compCd || "",
				equipmentCd: EQM2070_R01Filter.equipmentCd || "",
				judgeEmpno: editEQM2070_R01?.judgeEmpno || "",
				judgeEmpNm: editEQM2070_R01?.judgeEmpNm || "",
			};

			const detailPayload: EqmInspectResultDetailDto[] = currentFlat.map((row) => ({
				...payloadBase,
				...row,
				inspectDt: formatDate(row.inspectDt),
			}));
			const savePayload: EQM2070_R01Dto = {
				inspectResultHeaderDto: {
					...defaultEqmInspectResultHeaderDto,
					...editEQM2070_R01,
					compCd: EQM2070_R01Filter.compCd || "",
					equipmentCd: EQM2070_R01Filter.equipmentCd || "",
					inspectDt: `${EQM2070_R01Filter.inspectDt}-01`,
					remark: editEQM2070_R01?.remark || "",
				},
				inspectResultDetailDtoList: detailPayload,
			};

			const response = validateString(editEQM2070_R01?.inspectNo || "")
				? await patchEQM2070_R01(savePayload)
				: await postEQM2070_R01(savePayload);

			if (!response?.data.successFlag) return;

			showSuccess("점검결과 저장이 완료되었습니다.");
			setMessage("점검결과 저장이 완료되었습니다.");
			setTimeout(() => setMessage(""), 5000);

			if ((response.data.data?.length ?? 0) > 0) {
				const savedHeader = (
					response.data.data[0] as { inspectResultHeaderDto?: EqmInspectResultHeaderDto }
				)?.inspectResultHeaderDto;
				if (savedHeader?.inspectNo) {
					setSelectedEQM2070_R01(savedHeader);
					if (EQM2070_R01List.length === 0) {
						setEQM2070_R01List([savedHeader]);
					}
				}
				await fetchDetailList(savedHeader?.inspectNo || editEQM2070_R01?.inspectNo || "");
			}
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM2070_R01DetailPivotList, EQM2070_R01Filter.compCd, EQM2070_R01Filter.equipmentCd, EQM2070_R01Filter.inspectDt, EQM2070_R01List, editEQM2070_R01, setLoadingSpinner, setMessage, setSelectedEQM2070_R01, setEQM2070_R01List, fetchDetailList]);

	const handlePrintClick = useCallback(async () => {
		if (!selectedEQM2070_R01?.inspectNo) {
			showWarning("출력할 점검결과를 선택하세요.");
			setMessage("출력할 점검결과를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_EQM2070_R01(selectedEQM2070_R01.inspectNo);
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedEQM2070_R01, printPdfFromBlob, setLoadingSpinner, setMessage]);

	const handleExcelClick = useCallback(async () => {
		if (!selectedEQM2070_R01) {
			showWarning("선택된 점검결과가 없습니다.");
			return;
		}
		setLoadingSpinner(true);
		const response = await downloadExcel_EQM2070_R01(
			"",
			selectedEQM2070_R01.inspectNo,
			EQM2070_R01List.length ?? 0,
		);
		downloadSettingExcel({
			responseData: response.data.data,
			fileName: "설비점검결과 등록-excel.xlsx",
		});
		setLoadingSpinner(false);
	}, [selectedEQM2070_R01, EQM2070_R01List, setLoadingSpinner]);

	// 점검년월 또는 설비 탭 변경 시 헤더·마스터·상세 재조회
	useEffect(() => {
		const searchKey = `${EQM2070_R01Filter.inspectDt}|${EQM2070_R01Filter.equipmentCd}`;
		if (prevSearchKeyRef.current === searchKey) return;
		prevSearchKeyRef.current = searchKey;
		if (!EQM2070_R01Filter.compCd || !EQM2070_R01Filter.equipmentCd) return;
		void fetchData();
	}, [EQM2070_R01Filter.inspectDt, EQM2070_R01Filter.equipmentCd, EQM2070_R01Filter.compCd, fetchData]);

	// 신규 등록(점검번호 없음) 시 점검코드 마스터 기준 빈 pivot 그리드 표시
	useEffect(() => {
		if (!selectedEQM2070_R01 || selectedEQM2070_R01.inspectNo) return;
		if (inspectMasterRef.current.length === 0) return;
		hasOriginalFlatDetailRef.current = false;
		originalFlatDetailListRef.current = [];
		applyDetailPivot([], inspectMasterRef.current);
	}, [selectedEQM2070_R01, applyDetailPivot]);

	return {
		clickable,
		setClickable,
		handlePrintClick,
		handleExcelClick,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
	};
};
